import React from 'react'
import { observer } from 'mobx-react-lite'
import { getSnapshot, Instance } from 'mobx-state-tree'
import { ISelectorOption, useStores } from '@trivie/core'
import { SubscriptionProduct } from '@trivie/core/src/models/subscriptions/SubscriptionProduct'
import { AutocompleteSelector } from '../../../../components/autocomplete-selector'
import { RadioCard } from './radio-card'

interface SubscriptionContentProps {
  form: Instance<typeof SubscriptionProduct>
  editingProduct?: any
  isEditMode?: boolean
}

export const SubscriptionContent: React.FC<SubscriptionContentProps> = observer(
  ({ form, editingProduct, isEditMode }) => {
    const { manageUserStore } = useStores()
    const { userProfile } = manageUserStore

    const typeMap: Record<string, string> = {
      topic: 'topics',
      quiz: 'quizzes',
      path: 'learning paths',
    }

    const preselectIds = editingProduct?.content?.length
      ? editingProduct.content.map((c: any) => ({
          id: c.id,
          type: typeMap[c.type.toLowerCase()] ?? c.type,
        }))
      : undefined

    const handleSelectionChange = (selected: ISelectorOption[]) => {
      form.set(
        'content',
        selected.map((item) => ({
          id: item.id,
          type: item.type,
          label: item.label,
          subLabel: item.subLabel ?? '',
          photo_url: item.photo_url ?? undefined,
          parentId: item.parentId ?? undefined,
          childCount: item.childCount ?? undefined,
          checked: true,
          subItems:
            item.subItems?.map((si) => ({
              id: si.id,
              type: si.type,
              label: si.label,
              subLabel: si.subLabel ?? '',
              parentId: si.parentId ?? undefined,
              checked: false,
            })) ?? null,
        })),
      )
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isEditMode && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 20, marginBottom: 20 }}>
            <RadioCard
              selected={form.change_applies_to === 'all'}
              onClick={() => form.set('change_applies_to', 'all')}
              label="All subscribers"
              description="Update content for everyone, including current subscribers"
            />
            <RadioCard
              selected={form.change_applies_to === 'new'}
              onClick={() => form.set('change_applies_to', 'new')}
              label="New subscribers only"
              description="Current subscribers keep their existing content unchanged"
            />
          </div>
        )}
        <AutocompleteSelector
          variant="content"
          open
          noOptionsText="No content."
          filterExcludeTypes={
            userProfile.workspace_settings.learning_paths_enabled ? [] : ['learning paths']
          }
          preselectIds={preselectIds}
          defaultOptions={
            form.content.length > 0
              ? (getSnapshot(form.content) as unknown as ISelectorOption[])
              : undefined
          }
          onSelectionChange={handleSelectionChange}
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        />
      </div>
    )
  },
)
